import React, { FC, Fragment, useEffect } from 'react';
import ReactDOMServer from 'react-dom/server';
import { faLocationDot, faPlaneArrival, faPlaneDeparture, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Itinerary, LdsDealDetails } from '../../ts/interfaces/OphirResponse';
import { useTranslation } from 'react-i18next';
import { cssForPrint } from '../../constants';
import { generateItineraryHtml } from '../../service';
import OphirCarouselCard from './OphirCarouselCard';
import OphirSummaryCard from './OphirSummaryCard';
import OphirFlightCard from './OphirFlightCard';
import OphirIncludeCard from './OphirIncludeCard';
import OphirItineraty2 from './OphirItineraty2';
import { htmlToBase64 } from '../../utils';

interface OphirItineraryCardProps {
  itinerary: Itinerary;
  details?: LdsDealDetails;
}


const OphirItineraryCard: FC<OphirItineraryCardProps> = ({ itinerary, details }) => {

  const [t] = useTranslation() as unknown as [(key: string) => string];

  const [activeIndex, setActiveIndex] = React.useState(0);
  const [isDownloading, setIsDownloading] = React.useState(false);
  useEffect(() => {
    const itineraryListHeight = document.querySelector('.itinerary-list')?.clientHeight;
    document
      .querySelector('.itinerary-content')
      ?.setAttribute('style', `max-height: ${itineraryListHeight}px`);
  }, []);

  const onPressLocation = (index: number) => {
    const content = document.querySelector(`#content${index}`) as HTMLElement;
    document
      .querySelector('.itinerary-content')
      ?.scrollTo({ top: content!.offsetTop, behavior: 'smooth' });
  };

  const onScrollContent = () => {
    const childTopPositons = Array.from(document.getElementsByClassName('itinerary-text')).map(
      (el: any) => el.offsetTop
    );
    const top = document.querySelector('.itinerary-content')?.scrollTop!;

    const newActiveIndex = childTopPositons.findIndex((el) => el > top);

    setActiveIndex(newActiveIndex > 0 ? newActiveIndex - 1 : 0);
  };

  const cleanDayList = (dayDesc: string): string => {
    return dayDesc?.replace(/^ДЕНЬ \d+[:.\s–-]*\s*/, '').trimStart();
  };

  const onClickDownload = async () => {
    if (!details || isDownloading) return;

    setIsDownloading(true);

    const cardFirstHtml = ReactDOMServer.renderToStaticMarkup(
      <div className="oph-details-first mb-20">
        <OphirCarouselCard
          hotel={details.hotel}
          soldOut={details.status === 'RQ'}
        />
        <OphirSummaryCard details={details} />
      </div>
    );

    const cardFlightHtml = ReactDOMServer.renderToStaticMarkup(
      <div id='oph-details-flight'>
        <div className="title">{t('flight.informattion_about')}</div>
        <OphirFlightCard
          flight={details.flight.departure}
          returnFlight={details.flight.arrival}
          icon={faPlaneDeparture}
          title={t('flight.departure')}
        />
        <OphirFlightCard
          flight={details.flight.returnDeparture}
          returnFlight={details.flight.returnArrival}
          icon={faPlaneArrival}
          title={t('flight.arrival')}
        />
      </div>
    );

    const includeHtml = ReactDOMServer.renderToStaticMarkup(
      <div style={{ pageBreakBefore: 'always' }}>
        <OphirIncludeCard include={details.includeInfo} />
      </div>
    );

    const itineraryHtml = ReactDOMServer.renderToStaticMarkup(
      <OphirItineraty2 itinerary={itinerary} />
    );

    const html = `<html><head>${cssForPrint}</head><body>${cardFirstHtml}${cardFlightHtml}${includeHtml}${itineraryHtml}</body></html>`;

    const htmlRep = html.
      replaceAll(/https:\/\/[^\/]+\/backend\/s3/g, 'https://s3-eu-west-1.amazonaws.com/')

    const base64Html = htmlToBase64(htmlRep);

    try {
      await generateItineraryHtml(base64Html, +itinerary.itineraryUrl.replace(/[^0-9]/g, ''));
    } finally {
      setIsDownloading(false);
    }
  };


  return (
    <Fragment>
      <div className="title">
        <span>{t('tour.info_about')} - </span>

        <span
          style={{
            position: 'relative',
            display: 'inline-block',
            paddingRight: '20px',
          }}
        >
          <a
            className="text-underline color-contrast"
            rel="noreferrer"
            href="javascript:void(0)"
            onClick={onClickDownload}
          >
            {t('tour.download_way')}
          </a>
          {isDownloading && (
            <FontAwesomeIcon
              icon={faSpinner as any}
              spin
              style={{
                position: 'absolute',
                right: -10,
                top: 0,

              }}
            />
          )}
        </span>
      </div>

      {!!itinerary.items.length && (
        <div className="default-shadow itinerary mb-20">
          <div className="itinerary-list">
            {itinerary.items.map((item, index) => (
              <div
                className={'itinerary-item ' + (activeIndex === index ? 'active' : '')}
                key={index}
                onClick={() => onPressLocation(index)}
              >
                <span className="day">
                  {t('day')} {item.details.day}
                </span>
                <span className="icon">
                  <FontAwesomeIcon icon={faLocationDot as any} />
                </span>
                <span className="item-location">{cleanDayList(item.itemDesc)}</span>
              </div>
            ))}
          </div>
          <div className="itinerary-content" onScroll={onScrollContent}>
            {itinerary.items.map((item, index) => (
              <div className="itinerary-text" key={index} id={'content' + index}>
                <div>
                  <div className="text-bold mb-10">
                    {item.details.day}
                  </div>
                  <img src={item.details.image.link} alt={item.details.image.alt} />
                  {item.details.description}
                  <div>
                    {item.details?.addons
                      .filter((addon) => addon.DescLang.length)
                      .map((addon) => (
                        <div className="itinerary-desc">
                          <span className="text-bold"> {addon.DescLang[0].Desc} </span>
                          <br />
                          {addon.DescLang[0].ProductDesc}
                          <br />
                          {addon.Tags}
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Fragment>
  );
};

export default OphirItineraryCard;
